import { supabase } from './auth';
import { RecoveryMetrics, SleepMetrics, WorkoutData } from '../types';

/** Thrown when Terra isn't configured on the server (missing keys). */
export class TerraUnavailableError extends Error {}

export const connectTerra = async (): Promise<void> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');

  const response = await fetch('/.netlify/functions/terra-auth', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      reference_id: session.user.id,
      origin: window.location.origin,
    }),
  });

  if (response.status === 503) {
    throw new TerraUnavailableError('Wearable connections are not configured yet');
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.error || `Terra auth error: ${response.status}`);
  }

  const { url } = await response.json();
  window.location.href = url;
};

/**
 * Handle the redirect back from the Terra widget:
 * /auth/terra/callback?user_id=<terra id>&reference_id=<our uid>&resource=GARMIN
 */
export const handleTerraCallback = async (params: URLSearchParams): Promise<void> => {
  const terraUserId = params.get('user_id');
  const referenceId = params.get('reference_id');
  const resource = params.get('resource');

  if (!terraUserId) throw new Error('Terra callback missing user_id');

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No authenticated session found');
  if (referenceId && referenceId !== session.user.id) {
    throw new Error('Terra callback reference mismatch');
  }

  const { data: updated, error } = await supabase
    .from('users')
    .update({
      terra_user_id: terraUserId,
      terra_provider: resource ?? 'GARMIN',
      updated_at: new Date().toISOString(),
    })
    .eq('id', session.user.id)
    .select('id');

  if (error) throw error;
  if (!updated || updated.length === 0) {
    throw new Error('Your profile is not set up yet, so the wearable connection could not be saved. Complete onboarding first, then reconnect.');
  }
};

export interface TerraData {
  sleep: SleepMetrics;
  recovery: RecoveryMetrics;
  workouts: WorkoutData[];
}

export const fetchTerraData = async (terraUserId: string): Promise<TerraData | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');

  const response = await fetch(
    `/.netlify/functions/terra-data?terra_user_id=${encodeURIComponent(terraUserId)}`,
    { headers: { Authorization: `Bearer ${session.access_token}` } }
  );
  if (!response.ok) {
    throw new Error(`Terra data error: ${response.status}`);
  }

  const payload = await response.json();
  if (!payload.sleep) return null;

  return {
    sleep: {
      duration: payload.sleep.durationSeconds ?? 0,
      quality: payload.sleep.quality ?? 0,
      readiness: 0, // Garmin has no Oura-style readiness; engines fall back to the composite
      timestamp: payload.sleep.timestamp,
    },
    recovery: {
      hrv: payload.sleep.hrvMs ?? 0,
      restingHeartRate: payload.sleep.restingHeartRateBpm ?? 0,
      bodyTemperature: payload.sleep.temperatureDeviationC ?? 0,
      timestamp: payload.sleep.timestamp,
    },
    workouts: (payload.workouts ?? []).map((w: {
      id: string; type: string; durationSeconds: number;
      distanceMeters: number | null; averageHeartRate: number | null; timestamp: string;
    }) => ({
      id: w.id,
      duration: w.durationSeconds,
      intensity: w.averageHeartRate
        ? w.averageHeartRate < 120 ? 'low' : w.averageHeartRate < 150 ? 'moderate' : 'high'
        : 'moderate',
      type: w.type,
      timestamp: w.timestamp,
      distance: w.distanceMeters ?? undefined,
      averageHeartRate: w.averageHeartRate ?? undefined,
    })),
  };
};

/**
 * Merge wearable workouts into an existing list, skipping entries that look
 * like duplicates of ones we already have (same day, duration within 2 min)
 * — e.g. a ride that reached us via both Strava and Garmin.
 */
export const mergeWorkouts = (base: WorkoutData[], extra: WorkoutData[]): WorkoutData[] => {
  const isDupe = (w: WorkoutData) =>
    base.some(
      (b) =>
        b.timestamp.split('T')[0] === w.timestamp.split('T')[0] &&
        Math.abs(b.duration - w.duration) <= 120
    );
  return [...base, ...extra.filter((w) => !isDupe(w))].sort((a, b) =>
    b.timestamp.localeCompare(a.timestamp)
  );
};
